/**
 * useDashboard Hook
 *
 * Provides access to donor dashboard data including overview, charts, and community data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getDashboardOverview, getDashboardCharts } from '@/services/api/dashboardService';
import {
  getCommunityImpact,
  getCommunityUpdates,
  getUpcomingEvents,
} from '@/services/api/communityService';
import { cacheService } from '@/services/cache/cacheService';
import { useCompany } from './useCompany';
import type {
  DashboardOverviewResponse,
  DashboardChartsResponse,
  DashboardCommunityResponse,
  DashboardChartsOptions,
} from '@/types/dashboard.types';

/**
 * Dashboard overview hook options
 */
export interface UseDashboardOverviewOptions {
  companyId?: number;
  limit?: number;
  autoFetch?: boolean;
  enableCache?: boolean;
  cacheTTL?: number; // Cache time-to-live in milliseconds
}

/**
 * Dashboard overview hook result
 */
export interface UseDashboardOverviewResult {
  data: DashboardOverviewResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Dashboard charts hook options
 */
export interface UseDashboardChartsOptions extends DashboardChartsOptions {
  companyId?: number;
  autoFetch?: boolean;
  enableCache?: boolean;
  cacheTTL?: number;
}

/**
 * Dashboard charts hook result
 */
export interface UseDashboardChartsResult {
  data: DashboardChartsResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Dashboard community hook options
 */
export interface UseDashboardCommunityOptions {
  companyId?: number;
  eventLimit?: number;
  announcementLimit?: number;
  autoFetch?: boolean;
  enableCache?: boolean;
  cacheTTL?: number;
}

/**
 * Dashboard community hook result
 */
export interface UseDashboardCommunityResult {
  data: DashboardCommunityResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch dashboard overview data
 * Includes summary statistics, recent donations, recurring donations, and tax receipts
 *
 * @param options - Configuration options
 * @returns Dashboard overview data and control functions
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useDashboardOverview({
 *   limit: 10,
 *   enableCache: true,
 * });
 * ```
 */
export function useDashboardOverview(
  options?: UseDashboardOverviewOptions
): UseDashboardOverviewResult {
  const { companyIdNumber } = useCompany();

  const {
    companyId = companyIdNumber,
    limit = 10,
    autoFetch = true,
    enableCache = true,
    cacheTTL = 30 * 60 * 1000, // 30 minutes default
  } = options || {};

  const [data, setData] = useState<DashboardOverviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);
  const isFetchingRef = useRef(false);

  const fetchOverview = useCallback(async () => {
    if (!companyId) {
      setData(null);
      setIsLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      return;
    }

    const cacheKey = `dashboard:overview:${companyId}:${limit}`;

    setIsLoading(true);
    setError(null);
    isFetchingRef.current = true;

    try {
      // Check cache first
      if (enableCache) {
        const cached = cacheService.get<DashboardOverviewResponse>(cacheKey);
        if (cached) {
          setData(cached);
          setIsLoading(false);
          return;
        }
      }

      // Fetch from API
      const response = await getDashboardOverview(companyId, limit);
      setData(response);

      // Cache the result
      if (enableCache) {
        cacheService.set(cacheKey, response, { ttl: cacheTTL });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch dashboard overview');
      setError(error);
      console.error('[useDashboardOverview] Error:', error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [companyId, limit, enableCache, cacheTTL]);

  useEffect(() => {
    if (autoFetch && companyId) {
      fetchOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, limit, autoFetch]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchOverview,
  };
}

/**
 * Hook to fetch dashboard charts data
 * Includes donation trends and category breakdown
 *
 * @param options - Configuration options
 * @returns Dashboard charts data and control functions
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useDashboardCharts({
 *   period: 'monthly',
 *   months: 12,
 *   enableCache: true,
 * });
 * ```
 */
export function useDashboardCharts(options?: UseDashboardChartsOptions): UseDashboardChartsResult {
  const { companyIdNumber } = useCompany();

  const {
    companyId = companyIdNumber,
    period = 'monthly',
    months = 12,
    autoFetch = true,
    enableCache = true,
    cacheTTL = 30 * 60 * 1000, // 30 minutes default (charts update less frequently)
  } = options || {};

  const [data, setData] = useState<DashboardChartsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);
  const isFetchingRef = useRef(false);

  const fetchCharts = useCallback(async () => {
    if (!companyId) {
      setData(null);
      setIsLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      return;
    }

    const cacheKey = `dashboard:charts:${companyId}:${period}:${months}`;

    setIsLoading(true);
    setError(null);
    isFetchingRef.current = true;

    try {
      // Check cache first
      if (enableCache) {
        const cached = cacheService.get<DashboardChartsResponse>(cacheKey);
        if (cached) {
          setData(cached);
          setIsLoading(false);
          return;
        }
      }

      // Fetch from API
      const response = await getDashboardCharts(companyId, { period, months });
      setData(response);

      // Cache the result
      if (enableCache) {
        cacheService.set(cacheKey, response, { ttl: cacheTTL });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch dashboard charts');
      setError(error);
      console.error('[useDashboardCharts] Error:', error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [companyId, period, months, enableCache, cacheTTL]);

  useEffect(() => {
    if (autoFetch && companyId) {
      fetchCharts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, period, months, autoFetch]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchCharts,
  };
}

/**
 * Hook to fetch dashboard community data
 * Includes impact metrics, upcoming events, and announcements
 *
 * @param options - Configuration options
 * @returns Dashboard community data and control functions
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useDashboardCommunity({
 *   eventLimit: 5,
 *   announcementLimit: 5,
 *   enableCache: true,
 * });
 * ```
 */
export function useDashboardCommunity(
  options?: UseDashboardCommunityOptions
): UseDashboardCommunityResult {
  const { companyIdNumber } = useCompany();

  const {
    companyId = companyIdNumber,
    eventLimit = 5,
    announcementLimit = 5,
    autoFetch = true,
    enableCache = true,
    cacheTTL = 15 * 60 * 1000, // 15 minutes default (community data updates slowly)
  } = options || {};

  const [data, setData] = useState<DashboardCommunityResponse | null>(null);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);
  const isFetchingRef = useRef(false);

  const fetchCommunity = useCallback(async () => {
    if (!companyId) {
      setData(null);
      setIsLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      return;
    }

    const cacheKey = `dashboard:community:${companyId}:${eventLimit}:${announcementLimit}`;

    setIsLoading(true);
    setError(null);
    isFetchingRef.current = true;

    try {
      // Check cache first
      if (enableCache) {
        const cached = cacheService.get<DashboardCommunityResponse>(cacheKey);
        if (cached) {
          setData(cached);
          setIsLoading(false);
          return;
        }
      }

      // Fetch all three community endpoints in parallel (auth is optional)
      const [impact, events, updates] = await Promise.all([
        getCommunityImpact(companyId),
        getUpcomingEvents(companyId),
        getCommunityUpdates(companyId),
      ]);

      const response: DashboardCommunityResponse = { impact, events, updates };
      setData(response);

      // Cache the result
      if (enableCache) {
        cacheService.set(cacheKey, response, { ttl: cacheTTL });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch dashboard community');
      setError(error);
      console.error('[useDashboardCommunity] Error:', error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [companyId, eventLimit, announcementLimit, enableCache, cacheTTL]);

  useEffect(() => {
    if (autoFetch && companyId) {
      fetchCommunity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, eventLimit, announcementLimit, autoFetch]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchCommunity,
  };
}
